/**
 * shortTermMemoryService.js
 * 
 * Serviço de Memória de Curto Prazo
 * Gerencia questões que o usuário errou para reforço de aprendizado.
 * 
 * Fluxo:
 * 1. Usuário erra questão → Captura automática
 * 2. Questão entra na memória de curto prazo
 * 3. Precisa de 5 acertos consecutivos para promover (CONSECUTIVE_CORRECT_TO_PROMOTE)
 * 4. Ao promover → Transfere para repetição espaçada (médio prazo)
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { STORAGE_KEYS, SHORT_TERM_MEMORY_CONFIG, getShortTermInterval } from '../constants/categories';
import { addExistingQuestionToSpaced } from './storageService';

// Gerar ID único
const generateId = () => `stm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ==================== FUNÇÕES DE STORAGE ====================

/**
 * Carrega todas as questões da memória de curto prazo
 */
export const loadShortTermQuestions = async () => {
    try {
        const data = await AsyncStorage.getItem(STORAGE_KEYS.SHORT_TERM_MEMORY);
        return data ? JSON.parse(data) : [];
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao carregar:', error);
        return [];
    }
};

/**
 * Salva todas as questões da memória de curto prazo
 */
export const saveShortTermQuestions = async (questions) => {
    try {
        await AsyncStorage.setItem(STORAGE_KEYS.SHORT_TERM_MEMORY, JSON.stringify(questions));
        return true;
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao salvar:', error);
        return false;
    }
};

// ==================== FUNÇÕES DE CAPTURA ====================

/**
 * Adiciona ou atualiza uma questão errada na memória de curto prazo
 * Chamada automaticamente quando o usuário erra uma questão
 * 
 * @param {Object} question - Questão original que foi errada
 * @returns {Object|null} - Questão adicionada/atualizada ou null em caso de erro
 */
export const captureWrongAnswer = async (question) => {
    try {
        const questions = await loadShortTermQuestions();
        const now = Date.now();

        // Verifica se a questão já existe na memória de curto prazo
        const existingIndex = questions.findIndex(
            q => q.originalQuestionId === question.id
        );

        if (existingIndex !== -1) {
            // Questão já existe - incrementa contador de erros e reseta acertos consecutivos
            const existing = questions[existingIndex];
            existing.errorCount += 1;
            existing.consecutiveCorrect = 0; // Resetar ao errar
            existing.totalAttempts += 1;
            existing.lastErrorAt = now;
            existing.lastAttemptAt = now;
            // Backfill: entradas legadas podem ter sido capturadas sem alternativas.
            if (!existing.alternatives || existing.alternatives.length === 0) {
                existing.alternatives = question.alternatives || [];
            }
            if (!existing.type) {
                existing.type = question.type || 'multiple-choice';
            }
            existing.attemptHistory.push({
                timestamp: now,
                isCorrect: false,
            });

            questions[existingIndex] = existing;
            await saveShortTermQuestions(questions);

            console.log(`[ShortTermMemory] Questão atualizada: ${existing.id}, erros: ${existing.errorCount}`);
            return existing;
        } else {
            // Nova questão errada - adicionar à memória de curto prazo
            const newQuestion = {
                id: generateId(),
                originalQuestionId: question.id,
                text: question.text || question.enunciado || 'Questão sem texto',
                category: question.categoria || question.category || 'GERAL',

                // Conteúdo da questão (necessário p/ praticar e promover c/ alternativas)
                type: question.type || 'multiple-choice',
                alternatives: question.alternatives || [],

                // Contadores
                errorCount: 1,
                consecutiveCorrect: 0,
                totalAttempts: 1,

                // Timestamps
                firstErrorAt: now,
                lastErrorAt: now,
                lastAttemptAt: now,

                // Revisão espaçada
                nextReviewAt: now, // Disponível imediatamente na primeira vez
                currentInterval: 0, // Sem intervalo ainda (primeira aparição)

                // Status
                status: 'active',
                promotedAt: null,

                // Histórico
                attemptHistory: [{
                    timestamp: now,
                    isCorrect: false,
                }],
            };

            questions.push(newQuestion);
            await saveShortTermQuestions(questions);

            console.log(`[ShortTermMemory] Nova questão capturada: ${newQuestion.id}`);
            return newQuestion;
        }
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao capturar erro:', error);
        return null;
    }
};

/**
 * Registra um acerto em uma questão da memória de curto prazo
 * Se atingir 5 acertos, promove para médio prazo
 * Após cada acerto, agenda próxima revisão com intervalo espaçado (3, 5, 8, 12 dias)
 * 
 * @param {string} originalQuestionId - ID da questão original
 * @returns {Object} - Resultado com status e dados
 */
export const recordCorrectAnswer = async (originalQuestionId) => {
    try {
        const questions = await loadShortTermQuestions();
        const index = questions.findIndex(q => q.originalQuestionId === originalQuestionId);

        if (index === -1) {
            // Questão não está na memória de curto prazo (nunca errou ou já foi promovida)
            return { found: false, promoted: false };
        }

        const question = questions[index];
        const now = Date.now();

        // Incrementa acertos consecutivos
        question.consecutiveCorrect += 1;
        question.totalAttempts += 1;
        question.lastAttemptAt = now;
        question.attemptHistory.push({
            timestamp: now,
            isCorrect: true,
        });

        // Verifica se atingiu o limite para promoção (5 acertos)
        if (question.consecutiveCorrect >= SHORT_TERM_MEMORY_CONFIG.CONSECUTIVE_CORRECT_TO_PROMOTE) {
            // PROMOVER para memória de médio prazo (repetição espaçada)
            question.status = 'promoted';
            question.promotedAt = now;

            // Adicionar ao sistema de repetição espaçada (com conteúdo completo p/ revisão)
            await addExistingQuestionToSpaced({
                id: question.originalQuestionId,
                text: question.text,
                category: question.category,
                type: question.type,
                alternatives: question.alternatives || [],
            });

            // Remove da memória de curto prazo
            questions.splice(index, 1);
            await saveShortTermQuestions(questions);

            console.log(`[ShortTermMemory] Questão PROMOVIDA: ${question.id}`);
            return {
                found: true,
                promoted: true,
                message: 'Parabéns! Questão promovida para Memória de Médio Prazo!',
                question,
            };
        }

        // Calcula próxima data de revisão baseado no número de acertos
        const daysToAdd = getShortTermInterval(question.consecutiveCorrect);
        const nextReviewAt = now + (daysToAdd * 24 * 60 * 60 * 1000);
        question.nextReviewAt = nextReviewAt;
        question.currentInterval = daysToAdd;

        // Atualiza questão
        questions[index] = question;
        await saveShortTermQuestions(questions);

        const remaining = SHORT_TERM_MEMORY_CONFIG.CONSECUTIVE_CORRECT_TO_PROMOTE - question.consecutiveCorrect;
        console.log(`[ShortTermMemory] Acerto registrado: ${question.consecutiveCorrect}/5, próxima revisão em ${daysToAdd} dias`);

        return {
            found: true,
            promoted: false,
            consecutiveCorrect: question.consecutiveCorrect,
            remaining,
            nextReviewAt,
            daysToNextReview: daysToAdd,
            question,
        };
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao registrar acerto:', error);
        return { found: false, error: true };
    }
};

// ==================== FUNÇÕES DE CONSULTA ====================

/**
 * Retorna questões ativas (não promovidas) ordenadas por prioridade
 * Inclui flag isAvailableForReview para indicar se pode ser praticada hoje
 * Prioridade: mais erros primeiro, depois mais recentes
 */
export const getActiveQuestions = async () => {
    try {
        const questions = await loadShortTermQuestions();
        const now = Date.now();

        // Filtra apenas questões ativas e adiciona flag de disponibilidade
        const active = questions
            .filter(q => q.status === 'active')
            .map(q => ({
                ...q,
                isAvailableForReview: !q.nextReviewAt || q.nextReviewAt <= now,
            }));

        // Ordena por prioridade (disponíveis primeiro, depois mais erros, depois mais recentes)
        active.sort((a, b) => {
            // Disponíveis primeiro
            if (a.isAvailableForReview !== b.isAvailableForReview) {
                return a.isAvailableForReview ? -1 : 1;
            }
            // Depois por número de erros (decrescente)
            if (b.errorCount !== a.errorCount) {
                return b.errorCount - a.errorCount;
            }
            // Depois por data do último erro (mais recente primeiro)
            return b.lastErrorAt - a.lastErrorAt;
        });

        return active;
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao buscar questões ativas:', error);
        return [];
    }
};

/**
 * Retorna apenas questões disponíveis para revisão agora
 * (onde nextReviewAt <= agora)
 */
export const getQuestionsReadyForReview = async () => {
    try {
        const questions = await getActiveQuestions();
        return questions.filter(q => q.isAvailableForReview);
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao buscar questões prontas:', error);
        return [];
    }
};

/**
 * Retorna estatísticas da memória de curto prazo
 */
export const getShortTermStats = async () => {
    try {
        const questions = await loadShortTermQuestions();
        const active = questions.filter(q => q.status === 'active');

        // Erros por categoria
        const errorsByCategory = {};
        active.forEach(q => {
            const cat = q.category || 'GERAL';
            errorsByCategory[cat] = (errorsByCategory[cat] || 0) + q.errorCount;
        });

        // Total de tentativas
        const totalAttempts = active.reduce((sum, q) => sum + q.totalAttempts, 0);
        const totalErrors = active.reduce((sum, q) => sum + q.errorCount, 0);

        // Questões prontas para praticar (pelo menos 1 erro)
        const readyToPractice = active.filter(q => q.errorCount >= 1).length;

        // Questões quase promovidas (a 1 acerto da promoção)
        const almostPromoted = active.filter(
            q => q.consecutiveCorrect >= SHORT_TERM_MEMORY_CONFIG.CONSECUTIVE_CORRECT_TO_PROMOTE - 1
        ).length;

        // Taxa de recuperação (acertos / tentativas)
        const totalCorrect = active.reduce((sum, q) => {
            return sum + q.attemptHistory.filter(a => a.isCorrect).length;
        }, 0);
        const recoveryRate = totalAttempts > 0
            ? Math.round((totalCorrect / totalAttempts) * 100)
            : 0;

        return {
            totalQuestions: active.length,
            totalErrors,
            totalAttempts,
            readyToPractice,
            almostPromoted,
            recoveryRate,
            errorsByCategory,
        };
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao calcular stats:', error);
        return {
            totalQuestions: 0,
            totalErrors: 0,
            totalAttempts: 0,
            readyToPractice: 0,
            almostPromoted: 0,
            recoveryRate: 0,
            errorsByCategory: {},
        };
    }
};

/**
 * Retorna questões agrupadas por categoria
 */
export const getQuestionsByCategory = async () => {
    try {
        const questions = await getActiveQuestions();

        const grouped = {};
        questions.forEach(q => {
            const cat = q.category || 'GERAL';
            if (!grouped[cat]) {
                grouped[cat] = [];
            }
            grouped[cat].push(q);
        });

        return grouped;
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao agrupar por categoria:', error);
        return {};
    }
};

// ==================== FUNÇÕES DE MANUTENÇÃO ====================

/**
 * Remove questões expiradas (não praticadas por X dias)
 */
export const cleanupExpiredQuestions = async () => {
    try {
        const questions = await loadShortTermQuestions();
        const now = Date.now();
        const expirationMs = SHORT_TERM_MEMORY_CONFIG.DAYS_TO_EXPIRE * 24 * 60 * 60 * 1000;

        const validQuestions = questions.filter(q => {
            const timeSinceLastAttempt = now - q.lastAttemptAt;
            return timeSinceLastAttempt < expirationMs;
        });

        if (validQuestions.length !== questions.length) {
            const removed = questions.length - validQuestions.length;
            await saveShortTermQuestions(validQuestions);
            console.log(`[ShortTermMemory] ${removed} questões expiradas removidas`);
        }

        return validQuestions;
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao limpar expiradas:', error);
        return [];
    }
};

/**
 * Remove uma questão específica da memória de curto prazo
 */
export const removeQuestion = async (questionId) => {
    try {
        const questions = await loadShortTermQuestions();
        const filtered = questions.filter(q => q.id !== questionId);
        await saveShortTermQuestions(filtered);
        return true;
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao remover questão:', error);
        return false;
    }
};

/**
 * Limpa toda a memória de curto prazo
 */
export const clearAllQuestions = async () => {
    try {
        await AsyncStorage.removeItem(STORAGE_KEYS.SHORT_TERM_MEMORY);
        return true;
    } catch (error) {
        console.error('[ShortTermMemory] Erro ao limpar memória:', error);
        return false;
    }
};

// ==================== INTEGRAÇÃO COM FLUXO DE QUESTÕES ====================

/**
 * Processa a resposta do usuário
 * Chamada após o usuário responder uma questão em qualquer lugar do app
 * 
 * @param {Object} question - Questão respondida
 * @param {boolean} isCorrect - Se a resposta foi correta
 * @returns {Object} - Resultado do processamento
 */
export const processAnswer = async (question, isCorrect) => {
    if (isCorrect) {
        // Registra acerto (pode promover se atingir 5 acertos consecutivos)
        return await recordCorrectAnswer(question.id);
    } else {
        // Captura erro na memória de curto prazo
        const captured = await captureWrongAnswer(question);
        return {
            found: !!captured,
            captured: true,
            errorCount: captured?.errorCount || 1,
            question: captured,
        };
    }
};

export default {
    loadShortTermQuestions,
    saveShortTermQuestions,
    captureWrongAnswer,
    recordCorrectAnswer,
    getActiveQuestions,
    getQuestionsReadyForReview,
    getShortTermStats,
    getQuestionsByCategory,
    cleanupExpiredQuestions,
    removeQuestion,
    clearAllQuestions,
    processAnswer,
};
